<template>
  <div class="container" v-if="!isLoading">
    <div class="row">
      <div class="col-12">
        <TopNav class="mt-4" />
        <img class="mt-3" :srcset="`${logo} 3x`" />
        <p class="mt-4">
          {{ $t('SETTINGS.TEXT.GET_UNLIMITED_LIVE_STREAMING') }}
        </p>
        <HMActivatedTag
          v-if="isZoomConnected"
          :label="$t('SETTINGS.LABEL.CONNECTED')"
          class="my-3"
        />
      </div>
      <div class="col-12">
        <hr class="my-2" />
        <div
          class="d-flex flex-row justify-content-between align-items-center mt-4"
        >
          <h3>{{ $t('SETTINGS.LABEL.CONNECTED_ACCOUNTS') }}</h3>
          <label class="cursor-pointer" @click="connect">
            {{ $t('SETTINGS.BUTTON.CONNECT_ACCOUNT')
            }}<i class="fal fa-plus c-coral ml-2"></i>
          </label>
        </div>
        <HMTable class="mt-3" :items="zoom" :fields="zoomConnectionFields">
          <template slot="name" slot-scope="{ data }">
            <span>{{ getConnectionName(data) }}</span>
          </template>
          <template slot="is_default" slot-scope="{ data }">
            <HMCheck v-model="data.default" />
          </template>
          <template slot="zoom_webinars_enabled" slot-scope="{ data }">
            <HMCheck v-model="data.zoom_webinars_enabled" />
          </template>
          <template slot="connected" slot-scope="{ data }">
            <span>{{ $formatDate(data.created) }}</span>
          </template>
          <template slot="edit" slot-scope="{ data }">
            <HMIconButton
              :icon="['far', 'ellipsis-v']"
              :id="`popover-${data.id}`"
            />
            <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
              <HMPopoverMenuItem
                v-if="!data.default"
                @click="setZoomDefaultConnection(data.id)"
              >
                {{ $t('SETTINGS.LABEL.MAKE_DEFAULT') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem
                @click="showUpdateZoomConnectionNameModal(data)"
              >
                {{ $t('SETTINGS.LABEL.RENAME') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem @click="refreshZoomConnection(data.id)">
                {{ $t('SETTINGS.LABEL.REFRESH') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem
                v-if="canRemoveZoomConnection(data)"
                v-confirm="{
                  title: $t('SETTINGS.TITLE.DISCONNECT_ZOOM_CONNECTION'),
                  text: $t(
                    'SETTINGS.TEXT.DISCONNECT_ZOOM_CONNECTION_DESCRIPTION'
                  ),
                  confirmButton: $t('SETTINGS.BUTTON.DISCONNECT_FROM_ZOOM'),
                  confirm: () => removeZoomConnection(data.id),
                }"
              >
                <span class="d-flex justify-content-between align-items-center">
                  {{ $t('SETTINGS.LABEL.DISCONNECT') }}
                  <i class="far fa-trash-alt c-coral ml-4"></i>
                </span>
              </HMPopoverMenuItem>
            </HMPopover>
          </template>
        </HMTable>
        <hr class="mt-2" />
        <div class="h3 mt-4 mb-3">
          {{ $t('SETTINGS.TITLE.ADVANCED_OPTIONS') }}
        </div>
        <hr class="my-2" />
        <div
          class="form-group d-flex align-items-center justify-content-between my-3"
        >
          <label class="m-0 mr-3">
            {{
              $t('SETTINGS.LABEL.UPLOAD_ALL_ZOOM_RECORDINGS_TO', {
                applicationName,
              })
            }}
          </label>
          <HMSwitch
            v-model="zoomSettings.zoom_download_external_recordings"
            @change="update"
          />
        </div>
        <hr class="mt-0" />
        <div
          class="form-group d-flex align-items-center justify-content-between my-3"
        >
          <label class="m-0 mr-3">
            {{ $t('SETTINGS.LABEL.UPLOAD_ZOOM_AUDIO_RECORDINGS') }}
          </label>
          <HMSwitch
            v-model="zoomSettings.zoom_download_audio_recordings"
            @change="update"
          />
        </div>
        <hr class="mt-0" />
        <div
          class="form-group d-flex align-items-center justify-content-between my-3"
        >
          <label class="m-0 mr-3">
            {{
              $t('SETTINGS.LABEL.UPLOAD_CLOUD_RECORDINGS_AUTOMATICALLY', {
                applicationName,
              })
            }}
          </label>
          <HMSwitch
            v-model="zoomSettings.zoom_auto_upload_recordings"
            @change="update"
          />
        </div>
        <hr class="my-2" />
        <div
          class="form-group d-flex align-items-center justify-content-between my-3"
        >
          <label class="m-0 mr-3">
            {{
              $t('SETTINGS.LABEL.START_ALL_RECORDINGS_AUTOMATICALLY_ON', {
                applicationName,
              })
            }}
          </label>
          <HMSwitch
            v-model="zoomSettings.zoom_auto_start_recording"
            @change="update"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import { errorToMessage } from '@/core/utils/errors';
import { getConfig } from '@/web/utils/configuration';
import { replaceRedirectURLWithCurrentDomain } from '@/web/utils/url';
import { HMRenameModal } from '../../../components/common';
import { mapGetters } from 'vuex';
import UpgradeYourPlanModal from '../../../components/settings/UpgradeYourPlanModal';

export default Vue.extend({
  name: 'ZoomIntegration',
  components: {
    TopNav,
  },
  computed: {
    ...mapGetters({
      teacher: 'user/teacher',
    }),
    applicationName() {
      return getConfig().applicationName;
    },
    isZoomConnected() {
      return this.zoom?.length > 0;
    },
    zoomConnectionFields() {
      return [
        {
          name: 'name',
          title: this.$t('SETTINGS.TABLE.NAME'),
        },
        {
          name: 'is_default',
          title: this.$t('SETTINGS.TABLE.DEFAULT'),
        },
        {
          key: 'zoom_meetings_capacity',
          title: this.$t('SETTINGS.TABLE.MEETING_CAPACITY'),
        },
        {
          name: 'zoom_webinars_enabled',
          title: this.$t('SETTINGS.TABLE.WEBINAR_ENABLED'),
        },
        {
          key: 'zoom_webinars_capacity',
          title: this.$t('SETTINGS.TABLE.WEBINAR_CAPACITY'),
        },
        {
          name: 'connected',
          title: this.$t('SETTINGS.TABLE.CONNECTED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    upgradePlanDescription() {
      return this.$tc(
        'SETTINGS.TEXT.YOUR_PLAN_SUPPORTS_UP_TO_X_ZOOM_CONNECTIONS_VARIANT',
        this.teacher.max_zoom_connections,
        {
          connectionsLimit: this.teacher.max_zoom_connections,
        }
      );
    },
  },
  data() {
    return {
      logo: '/assets/images/integrations/zoom@3x.png',
      isLoading: true,
      inProgress: false,
      zoom: null,
      zoomSettings: null,
    };
  },
  methods: {
    canRemoveZoomConnection(zoomConnection) {
      if (zoomConnection.default && this.zoom?.length > 1) {
        return false;
      }

      return true;
    },
    getConnectionName(zoomConnection) {
      if (zoomConnection.zoom_email) {
        return `${zoomConnection.name} (${zoomConnection.zoom_email})`;
      }
      return zoomConnection.name;
    },
    async update() {
      if (this.inProgress) return;

      this.inProgress = true;
      try {
        await this.$store.dispatch(
          'setting/updateZoomSettings',
          this.zoomSettings
        );
        this.$store.dispatch('user/loadUserInfo');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.inProgress = false;
      }
    },
    connect() {
      if (this.zoom?.length >= this.teacher.max_zoom_connections) {
        this.$modal.show(UpgradeYourPlanModal, {
          title: 'SETTINGS.TEXT.UPGRADE_YOUR_PLAN',
          description: this.upgradePlanDescription,
        });
        return;
      }
      const responseType = 'code';
      const clientId = process.env.VUE_APP_ZOOM_CLIENT_ID;
      const redirectURL = encodeURIComponent(
        replaceRedirectURLWithCurrentDomain(
          process.env.VUE_APP_ZOOM_REDIRECT_URL
        )
      );
      window.location = `https://zoom.us/oauth/authorize?response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectURL}`;
    },
    checkForZoomOAuthCode() {
      const code = this.$route.query.code;
      if (!code) return;

      const query = { ...this.$route.query };
      delete query.code;
      this.$router.replace({ query });

      this.addZoomConnection(code);
    },
    async addZoomConnection(code) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/addZoomConnection', code);
        this.zoom = await this.$store.dispatch('setting/getZoomConnections');
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.ZOOM_SUCCESSFULLY_CONNECTED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async setZoomDefaultConnection(zoomConnectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/setZoomDefaultConnection',
          zoomConnectionId
        );
        this.zoom = await this.$store.dispatch('setting/getZoomConnections');
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.ZOOM_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async updateZoomConnectionName(zoomConnectionId, name) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('setting/setZoomConnectionName', {
          zoomConnectionId,
          name,
        });
        this.zoom = await this.$store.dispatch('setting/getZoomConnections');
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.ZOOM_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async refreshZoomConnection(zoomConnectionId) {
      try {
        await this.$store.dispatch(
          'setting/refreshZoomConnection',
          zoomConnectionId
        );
        this.zoom = await this.$store.dispatch('setting/getZoomConnections');
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.ZOOM_SUCCESSFULLY_REFRESHED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
    },
    async removeZoomConnection(zoomConnectionId) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(
          'setting/removeZoomConnection',
          zoomConnectionId
        );
        this.zoom = await this.$store.dispatch('setting/getZoomConnections');
        this.$store.dispatch('user/loadUserInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.ZOOM_SUCCESSFULLY_REMOVED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isLoading = false;
      }
    },
    showUpdateZoomConnectionNameModal(zoomConnection) {
      this.$modal.show(
        HMRenameModal,
        {
          value: zoomConnection.name,
        },
        {},
        {
          renamed: async name => {
            await this.updateZoomConnectionName(zoomConnection.id, name);
          },
        }
      );
    },
  },
  async mounted() {
    this.$store.dispatch('user/loadUserInfo');
    this.zoomSettings = await this.$store.dispatch('setting/getZoomSettings');
    this.zoom = await this.$store.dispatch('setting/getZoomConnections');
    this.checkForZoomOAuthCode();
    this.isLoading = false;
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.cursor-pointer {
  cursor: pointer;
}
</style>
